import { Suspense, lazy, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import './App.css'

import { AuthProvider }     from './contexts/AuthContext'
import { SettingsProvider, useSettings } from './contexts/SettingsContext'
import { CartProvider }     from './contexts/CartContext'
import { WishlistProvider } from './contexts/WishlistContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { ToastProvider }       from './contexts/ToastContext'
import { useReveal }        from './hooks/useReveal'

import Navbar          from './components/Navbar'
import CartDrawer      from './components/CartDrawer'
import GuestRoute      from './components/GuestRoute'
import ProtectedRoute  from './components/ProtectedRoute'
import AdminRoute      from './components/admin/AdminRoute'
import PageLoader      from './components/PageLoader'
import { PWAProvider } from './components/PWAInstall'
import PWAInstallBanner from './components/PWAInstallBanner'
import SEO, { organizationSchema } from './components/SEO'
import ErrorBoundary  from './components/ErrorBoundary'

/* Landing page — eager (critique pour le premier rendu) */
import Hero            from './components/Hero'
import Categories      from './components/Categories'
import FeaturedProducts from './components/FeaturedProducts'
import WhyUs           from './components/WhyUs'
import HowItWorks      from './components/HowItWorks'
import Stores          from './components/Stores'
import AppPromo        from './components/AppPromo'
import Footer          from './components/Footer'

/* Pages publiques — lazy loaded */
const Login          = lazy(() => import('./pages/auth/Login'))
const OAuthCallback  = lazy(() => import('./pages/auth/OAuthCallback'))
const Register       = lazy(() => import('./pages/auth/Register'))
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const ResetPassword  = lazy(() => import('./pages/auth/ResetPassword'))
const Catalogue      = lazy(() => import('./pages/Catalogue'))
const ProductDetail  = lazy(() => import('./pages/ProductDetail'))
const Checkout            = lazy(() => import('./pages/Checkout'))
const OrderConfirmation   = lazy(() => import('./pages/OrderConfirmation'))
const Orders              = lazy(() => import('./pages/Orders'))
const Profile        = lazy(() => import('./pages/Profile'))
const Wishlist        = lazy(() => import('./pages/Wishlist'))
const OrderTracking   = lazy(() => import('./pages/OrderTracking'))
const NotFound        = lazy(() => import('./pages/NotFound'))

/* Admin — lazy loaded (layout propre, sans Navbar site) */
const AdminDashboard      = lazy(() => import('./pages/admin/Dashboard'))
const AdminOrders         = lazy(() => import('./pages/admin/AdminOrders'))
const AdminProducts       = lazy(() => import('./pages/admin/AdminProducts'))
const AdminCategories     = lazy(() => import('./pages/admin/AdminCategories'))
const AdminUsers          = lazy(() => import('./pages/admin/AdminUsers'))
const AdminReviews        = lazy(() => import('./pages/admin/AdminReviews'))
const AdminCoupons        = lazy(() => import('./pages/admin/AdminCoupons'))
const AdminDeliveryZones  = lazy(() => import('./pages/admin/AdminDeliveryZones'))
const AdminAnalytics      = lazy(() => import('./pages/admin/AdminAnalytics'))
const AdminCRM            = lazy(() => import('./pages/admin/AdminCRM'))
const AdminInternalUsers  = lazy(() => import('./pages/admin/AdminInternalUsers'))
const PreparateurOrders   = lazy(() => import('./pages/admin/PreparateurOrders'))
const LivreurOrders       = lazy(() => import('./pages/admin/LivreurOrders'))
const AdminReporting      = lazy(() => import('./pages/admin/AdminReporting'))
const AdminAuditLog       = lazy(() => import('./pages/admin/AdminAuditLog'))
const AdminImport         = lazy(() => import('./pages/admin/AdminImport'))
const AdminLoyalty        = lazy(() => import('./pages/admin/AdminLoyalty'))
const QrConfirm           = lazy(() => import('./pages/QrConfirm'))
const ProfileAddresses    = lazy(() => import('./pages/ProfileAddresses'))
const Referral            = lazy(() => import('./pages/Referral'))
const Loyalty             = lazy(() => import('./pages/Loyalty'))
const CGU                 = lazy(() => import('./pages/legal/CGU'))
const PrivacyPolicy       = lazy(() => import('./pages/legal/PrivacyPolicy'))

/* ── Loader ── */

/* ── Landing ── */
function LandingContent() {
  const { lang } = useSettings()
  const location = useLocation()
  const didMount = useRef(false)
  useReveal()
  const desc = lang === 'fr'
    ? 'ABBUSINESS — Votre boutique en ligne premium en Guinée. Produits de qualité, livraison rapide, paiement à la livraison.'
    : 'ABBUSINESS — Your premium online store in Guinea. Quality products, fast delivery, cash on delivery.'

  useEffect(() => {
    const hash = location.hash   // ex: '#stores', '#products', ''
    if (hash) {
      // Attendre que les sections soient dans le DOM (lazy images, etc.)
      const scrollToHash = () => {
        const el = document.getElementById(hash.slice(1))
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 72
          window.scrollTo({ top, behavior: 'smooth' })
          setTimeout(() => window.__revealCheck?.(), 300)
        }
      }
      setTimeout(scrollToHash, 120)
      setTimeout(scrollToHash, 500) // retry si le DOM tardait
    } else if (!didMount.current) {
      // Premier montage sans hash → top de page
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
    didMount.current = true
  }, [location.hash])

  return (
    <div className="app">
      <SEO
        description={desc}
        url="/"
        jsonLd={[
          organizationSchema,
          {
            '@context': 'https://schema.org',
            '@type':    'WebSite',
            name:       'ABBUSINESS',
            url:        'https://abbusinessgn.com',
            potentialAction: {
              '@type':       'SearchAction',
              target:        'https://abbusinessgn.com/catalogue?search={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
        ]}
      />
      <Hero              lang={lang} />
      <Categories        lang={lang} />
      <FeaturedProducts  lang={lang} />
      <Stores            lang={lang} />
      <WhyUs             lang={lang} />
      <HowItWorks        lang={lang} />
      {/*
        | La section des temoignages a ete retiree de l'accueil.
        |
        | Elle portait quatre avis entierement inventes : Mamadou Diallo de
        | Ratoma louant un « Salon Prestige 5 Places » livre en 48 h,
        | Fatoumata Camara sa « Chaise Massage 4D Deluxe », Alpha Oumar Bah
        | son « Réfrigérateur Samsung 500L » depuis Labé, Mariama Kouyaté ses
        | rideaux, avec noms, quartiers, dates, produits et notes. Aucune de
        | ces personnes n'a ecrit cela, aucun de ces quatre produits n'existe
        | au catalogue, et le bandeau annoncait « 4,9/5 sur plus de 2 400 avis
        | vérifiés » sur une table d'avis vide.
        |
        | Ce n'est pas une question de style : des avis fabriques engagent la
        | responsabilite de l'entreprise qui les publie, et ce sont les
        | premiers a se retourner contre elle. La section reviendra quand la
        | moderation d'avis, qui existe deja dans l'administration, aura de
        | vrais avis a moderer.
      */}
      <AppPromo          lang={lang} />
      <Footer            lang={lang} />
    </div>
  )
}

/* ── Site shell (avec Navbar + CartDrawer) ── */
function SiteShell() {
  return (
    <>
      <Navbar />
      <CartDrawer />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/"                     element={<LandingContent />} />
            <Route path="/catalogue"            element={<Catalogue />} />
            <Route path="/produits/:slug"       element={<ProductDetail />} />
            <Route path="/auth/login"           element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/auth/register"        element={<GuestRoute><Register /></GuestRoute>} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/oauth-callback"       element={<OAuthCallback />} />
            <Route path="/auth/reset-password"  element={<ResetPassword />} />
            {/* Ouvert aux visiteurs : le tunnel gere lui-meme les deux cas,
                et exiger un compte ici etait l'obstacle qu'on retire. */}
            <Route path="/checkout"                               element={<Checkout />} />
            <Route path="/commandes/confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
            <Route path="/profile"                            element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/profile/adresses"                   element={<ProtectedRoute><ProfileAddresses /></ProtectedRoute>} />
            <Route path="/parrainage"                         element={<ProtectedRoute><Referral /></ProtectedRoute>} />
            <Route path="/fidelite"                           element={<ProtectedRoute><Loyalty /></ProtectedRoute>} />
            <Route path="/commandes"                          element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/ma-liste"             element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
            <Route path="/suivi/:orderNumber"         element={<OrderTracking />} />
            <Route path="/suivi"                      element={<OrderTracking />} />
            <Route path="/livraison/confirmer/:token" element={<QrConfirm />} />
            <Route path="/cgu"                        element={<CGU />} />
            <Route path="/confidentialite"            element={<PrivacyPolicy />} />
            <Route path="*"                           element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <PWAInstallBanner />
    </>
  )
}

/* ── Admin shell (sans Navbar site — AdminLayout intégré dans chaque page) ── */
function AdminShell() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader dark />}>
      <Routes>
        {/* Chaque route declare la capacite qu'elle exige. Le serveur la
            reevalue a chaque appel : ceci ne fait que masquer ce qui n'est
            pas permis, et eviter d'atterrir sur un ecran vide. */}
        <Route path="/admin"              element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/commandes"    element={<AdminRoute requires="orders.read"><AdminOrders /></AdminRoute>} />
        <Route path="/admin/produits"     element={<AdminRoute requires="products.read"><AdminProducts /></AdminRoute>} />
        <Route path="/admin/categories"   element={<AdminRoute requires="categories.write"><AdminCategories /></AdminRoute>} />
        <Route path="/admin/utilisateurs" element={<AdminRoute requires="users.read"><AdminUsers /></AdminRoute>} />
        <Route path="/admin/avis"         element={<AdminRoute requires="reviews.moderate"><AdminReviews /></AdminRoute>} />
        <Route path="/admin/coupons"      element={<AdminRoute requires="coupons.write"><AdminCoupons /></AdminRoute>} />
        <Route path="/admin/livraison"    element={<AdminRoute requires="delivery.write"><AdminDeliveryZones /></AdminRoute>} />
        <Route path="/admin/analytics"    element={<AdminRoute requires="analytics.read"><AdminAnalytics /></AdminRoute>} />
        <Route path="/admin/crm"         element={<AdminRoute requires="users.read"><AdminCRM /></AdminRoute>} />
        <Route path="/admin/reporting"    element={<AdminRoute requires="reporting.read"><AdminReporting /></AdminRoute>} />
        <Route path="/admin/staff"        element={<AdminRoute requires="staff.manage"><AdminInternalUsers /></AdminRoute>} />
        <Route path="/admin/journal"      element={<AdminRoute requires="audit.read"><AdminAuditLog /></AdminRoute>} />
        <Route path="/admin/import"       element={<AdminRoute requires="products.write"><AdminImport /></AdminRoute>} />
        <Route path="/admin/preparateur"  element={<AdminRoute requires="orders.prepare"><PreparateurOrders /></AdminRoute>} />
        <Route path="/admin/fidelite"      element={<AdminRoute requires="analytics.read"><AdminLoyalty /></AdminRoute>} />
        <Route path="/admin/livreur"      element={<AdminRoute requires="orders.deliver"><LivreurOrders /></AdminRoute>} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}

/* ── Router root — sépare admin / site ── */
function AppRouter() {
  const { pathname } = useLocation()
  const isAdmin = pathname.startsWith('/admin')
  return isAdmin ? <AdminShell /> : <SiteShell />
}

/* ── Root ── */
export default function App() {
  return (
    <HelmetProvider>
    <SettingsProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
            <NotificationProvider>
            <ToastProvider>
            <PWAProvider>
              <AppRouter />
            </PWAProvider>
            </ToastProvider>
            </NotificationProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </SettingsProvider>
    </HelmetProvider>
  )
}
